package kz.dentalcrm.app.data.network

import io.ktor.client.HttpClient
import io.ktor.client.request.header
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpMethod
import io.ktor.http.contentType
import io.ktor.http.headers
import io.ktor.http.path
import kz.dentalcrm.app.core.network.handle
import kz.dentalcrm.app.core.storage.Storage
import kz.dentalcrm.app.data.network.request_model.AddPatientBody
import kz.dentalcrm.app.data.network.request_model.AppointmentRequest
import kz.dentalcrm.app.data.network.response_model.AppointmentResponse
import kz.dentalcrm.app.data.network.response_model.CashBox
import kz.dentalcrm.app.data.network.response_model.PatientData
import kz.dentalcrm.app.data.network.response_model.PatientTreatment
import kz.dentalcrm.app.data.network.response_model.PatientsResponse
import kz.dentalcrm.app.data.network.response_model.employees.EmployeesResponse

class MainApi(
    private val httpClient: HttpClient,
    private val storage: Storage,
) {

    suspend fun getAppointments(
        dateFrom: String,
        dateTo: String,
        employeeId: String?
    ) = httpClient.handle<List<AppointmentResponse>> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path(
                "api/v1/appointments"
            )
            parameters.append("time_from", dateFrom)
            parameters.append("time_to", dateTo)
        }
        method = HttpMethod.Get
    }

    suspend fun addPatient(
        name: String,
        surname: String,
        phone: String
    ) = httpClient.handle<PatientData?> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path(
                "api/v1/patients"
            )
            setBody(
                AddPatientBody(
                    name = name,
                    surname = surname,
                    phone = phone
                )
            )
        }
        method = HttpMethod.Post
    }

    suspend fun getPatients(
        page: Int = 1,
        count: Int = 10
    ) = httpClient.handle<PatientsResponse?> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path(
                "api/v1/patients"
            )
            parameters.append("perPage", count.toString())
            parameters.append("page", page.toString())
        }
        method = HttpMethod.Get
    }

    suspend fun searchPatientByFilter(
        name: String,
        surname: String,
        phone: String
    ) = httpClient.handle<PatientsResponse?> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path(
                "api/v1/patients/search"
            )
            if (name.isNotEmpty()) {
                parameters.append("perPage", name)
            }
            if (surname.isNotEmpty()) {
                parameters.append("surname", surname)
            }
            if (phone.isNotEmpty()) {
                parameters.append("phone", phone)
            }
        }
        method = HttpMethod.Get
    }

    suspend fun search(
        search: String,
        page: Int = 1,
        perPage: Int = 10
    ) = httpClient.handle<PatientsResponse> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path(
                "api/v1/patients"
            )
            parameters.append("search", search)
            parameters.append("page", page.toString())
            parameters.append("perPage", perPage.toString())
        }
        method = HttpMethod.Get
    }

    suspend fun getPatient(
        id: Int
    ) = httpClient.handle<PatientData?> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path(
                "api/v1/patients/$id"
            )
        }
        method = HttpMethod.Get
    }

    suspend fun getEmployees(
        page: Int = 1,
        count: Int = 10
    ) = httpClient.handle<EmployeesResponse?> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path(
                "api/v1/employees"
            )
            parameters.append("perPage", count.toString())
            parameters.append("page", page.toString())
        }
        method = HttpMethod.Get
    }

    suspend fun createAppointment(appointmentRequest: AppointmentRequest) =
        httpClient.handle<Any?> {
            contentType(ContentType.Application.Json)
            url {
                headers {
                    header("Authorization", "Bearer ${storage.getAccessToken()}")
                }
                setBody(appointmentRequest)
                path(
                    "api/v1/appointments"
                )
            }
            method = HttpMethod.Post
        }

    suspend fun deleteAppointment(id: Int) =
        httpClient.handle<Any?> {
            contentType(ContentType.Application.Json)
            url {
                headers { header("Authorization", "Bearer ${storage.getAccessToken()}") }
                path("api/v1/appointments/$id")
            }
            method = HttpMethod.Delete
        }

    suspend fun updatePatient(
        id: Int,
        patientData: PatientData
    ) = httpClient.handle<Any?> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path("api/v1/patients/$id")
            setBody(patientData)
        }
        method = HttpMethod.Put
    }

    suspend fun deletePatient(id: Int) =
        httpClient.handle<Any?> {
            contentType(ContentType.Application.Json)
            url {
                headers { header("Authorization", "Bearer ${storage.getAccessToken()}") }
                path("api/v1/patients/$id")
            }
            method = HttpMethod.Delete
        }

    suspend fun getPositions() = httpClient.handle<List<kz.dentalcrm.app.data.network.response_model.employees.Position>> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path("api/v1/positions")
        }
        method = HttpMethod.Get
    }

    suspend fun getServicesArray() = httpClient.handle<List<kz.dentalcrm.app.data.network.response_model.services.Service>> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path("api/v1/get_services_array")
        }
        method = HttpMethod.Get
    }

    suspend fun addEmployee(employeeBody: kz.dentalcrm.app.data.network.request_model.AddEmployeeBody) =
        httpClient.handle<Any?> {
            contentType(ContentType.Application.Json)
            url {
                headers {
                    header("Authorization", "Bearer ${storage.getAccessToken()}")
                }
                path("api/v1/employees")
                setBody(employeeBody)
            }
            method = HttpMethod.Post
        }

    suspend fun getPermissions() = httpClient.handle<List<kz.dentalcrm.app.data.network.response_model.permissions.Permission>> {
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path("api/v1/permissions")
        }
        method = HttpMethod.Get
    }

    suspend fun getEmployee(id: Int) = httpClient.handle<kz.dentalcrm.app.data.network.response_model.employees.Employee?> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path("api/v1/employees/$id")
        }
        method = HttpMethod.Get
    }

    suspend fun deleteEmployee(id: Int) = httpClient.handle<Any?> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path("api/v1/employees/$id")
        }
        method = HttpMethod.Delete
    }

    suspend fun updateEmployee(id: Int, employeeBody: kz.dentalcrm.app.data.network.request_model.AddEmployeeBody) = httpClient.handle<Any?> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path("api/v1/employees/$id")
            setBody(employeeBody)
        }
        method = HttpMethod.Put
    }

    suspend fun getAppointment(id: Int) = httpClient.handle<AppointmentResponse> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path("api/v1/appointments/$id")
        }
        method = HttpMethod.Get
    }

    suspend fun updateAppointment(id: Int, appointmentRequest: AppointmentRequest) =
        httpClient.handle<Any?> {
            contentType(ContentType.Application.Json)
            url {
                headers {
                    header("Authorization", "Bearer ${storage.getAccessToken()}")
                }
                setBody(appointmentRequest)
                path("api/v1/appointments/$id")
            }
            method = HttpMethod.Put
        }

    suspend fun getCashBoxes() = httpClient.handle<List<CashBox>> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path("api/v1/cash-boxes-array")
        }
        method = HttpMethod.Get
    }

    suspend fun getAppointmentTreatments(appointmentId: Int) = httpClient.handle<List<PatientTreatment>> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path("api/v1/appointments/$appointmentId/treatments")
        }
        method = HttpMethod.Get
    }

    suspend fun getPatientLastAppointments(patientId: Int) = httpClient.handle<List<AppointmentResponse>> {
        contentType(ContentType.Application.Json)
        url {
            headers {
                header("Authorization", "Bearer ${storage.getAccessToken()}")
            }
            path("api/v1/get_patient_last_appointments/$patientId")
        }
        method = HttpMethod.Get
    }
}